import { publish, subscribe, EVENTS } from '@sendible/event-manager';
import { useTranslation } from 'react-i18next';
import { useState, useEffect } from 'react';
import { AI_ASSIST_QUICK_ACTIONS } from '../AiAssist/consts';
import { aIAssistantOpenWithContent, aIAssistantOpenWithNoContent } from '../AiAssist/pendoEvents';
import { ComposeToolbarDropdown, OptionItem } from '../ComposeToolbarDropdown';

const hashtags = 'compose-ai-hashtags';
const rephrase = 'compose-ai-rephrase';
const spelling = 'compose-ai-spelling';
const shorten = 'compose-ai-shorten';
const lengthen = 'compose-ai-lengthen';
const customise = 'compose-ai-customise';
const elementId = 'compose-ai-dropdown';

export const AiAssistDropdown = () => {
  const [isEnabled, setIsEnabled] = useState(false);
  const [composeId, setComposeId] = useState(null);
  const [composeText, setComposeText] = useState('');
  const [isDropdownVisible, setIsDropdownVisible] = useState(false);
  const { t } = useTranslation('ai_assist');

  const portalElementId = composeId && composeId !== 'compose-message' ? `${elementId}-${composeId}` : elementId;
  const portal = window.document.getElementById(portalElementId) as HTMLElement;

  const quickActionObj = AI_ASSIST_QUICK_ACTIONS.customiseActionsArray.find((tabObject) => tabObject.tab === composeId);

  const handleClick = (option: string) => {
    let quickAction = null;

    if (option === hashtags) quickAction = AI_ASSIST_QUICK_ACTIONS.hashtags;
    else if (option === rephrase) quickAction = AI_ASSIST_QUICK_ACTIONS.rephrase;
    else if (option === spelling) quickAction = AI_ASSIST_QUICK_ACTIONS.spelling;
    else if (option === shorten) quickAction = AI_ASSIST_QUICK_ACTIONS.shorten;
    else if (option === lengthen) quickAction = AI_ASSIST_QUICK_ACTIONS.lengthen;
    else if (option === customise) {
      if (quickActionObj && quickActionObj.action) quickAction = quickActionObj.action;
    }

    publish(EVENTS.SHOW_AI_ASSIST, { quickAction, composeText });
    window.pendo.track(aIAssistantOpenWithContent);

    setIsDropdownVisible(false);
  };

  const isCustomisationDisabled = !!composeText.length && composeId === 'compose-message';

  const optionList: OptionItem[] = [
    {
      id: customise,
      label: quickActionObj && quickActionObj.action ? t(quickActionObj.action.toLowerCase().replace(/\s/g, '-')) : t('customise'),
      testId: 'ai-option-customise',
      disabled: isCustomisationDisabled,
      tooltip: isCustomisationDisabled ? (t('disabled_title') as string) : undefined,
    },
    {
      id: hashtags,
      label: t('hashtags'),
      testId: 'ai-option-hashtags',
    },
    {
      id: rephrase,
      label: t('rephrase'),
      testId: 'ai-option-rephrase',
    },
    {
      id: spelling,
      label: t('spelling'),
      testId: 'ai-option-spelling',
    },
    {
      id: shorten,
      label: t('shorten'),
      testId: 'ai-option-shorten',
    },
    {
      id: lengthen,
      label: t('lengthen'),
      testId: 'ai-option-lengthen',
    },
  ];

  useEffect(() => {
    const unsubscribeOpenCompose = subscribe(EVENTS.COMPOSEBOX_OPENED, (data) => {
      setIsEnabled(!!data.showAiAssist);
    });

    const unsubscribeDropdown = subscribe(EVENTS.AI_ASSIST_CLICKED, (data) => {
      if (!data.text) {
        publish(EVENTS.SHOW_AI_ASSIST, { quickAction: null, composeText: '' });
        window.pendo.track(aIAssistantOpenWithNoContent);
      } else {
        setComposeId(data.composeId);
        setComposeText(data.text);

        setIsDropdownVisible((prev) => !prev);
      }
    });

    return () => {
      unsubscribeOpenCompose();
      unsubscribeDropdown();
    };
  }, []);

  if (!portal || !isEnabled) return null;

  return (
    <ComposeToolbarDropdown
      buttonId="compose-ai"
      isVisible={isDropdownVisible}
      onClose={() => setIsDropdownVisible(false)}
      onSelect={(option) => handleClick(option)}
      options={optionList}
      portal={portal}
    />
  );
};
